import { useState, useEffect } from 'react'
import { AccountCredentialForm } from '@/components/integrations/AccountCredentialForm'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { toast } from 'sonner'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import * as z from 'zod'
import type { EpicPmMetadata } from '@/lib/api/integrations/epic-pm.client'

const epicPmAdditionalSchema = z.object({
  defaultProperty: z.string().optional(),
})

export interface EpicPmConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function EpicPmConnectForm({
  isEditMode = false,
  onConnected,
}: EpicPmConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    accountIdentifier: string
  } | null>(null)
  const [defaultProperty, setDefaultProperty] = useState<string>('')
  const [isLoading, setIsLoading] = useState(isEditMode)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const epicPm = integrations.find(
          i =>
            i.type === INTEGRATIONS.EPIC_PM.TYPE ||
            i.name === INTEGRATIONS.EPIC_PM.NAME,
        )
        if (epicPm?.connected && epicPm.accountId) {
          setExistingCredential({ accountIdentifier: epicPm.accountId })
          if (epicPm.metadata?.defaultProperty) {
            setDefaultProperty(epicPm.metadata.defaultProperty as string)
          }
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    accountIdentifier: string
    password: string
    defaultProperty?: string
  }) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    const metadata: EpicPmMetadata = {
      ...(data.defaultProperty && { defaultProperty: data.defaultProperty }),
    }

    try {
      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.epicPm.updateAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.epicPm.updateCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Epic PM credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.epicPm.createAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.epicPm.createCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Epic PM integration connected successfully!')
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} Epic PM:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} Epic PM integration`,
      )
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <AccountCredentialForm
      onSubmit={handleSubmit}
      accountIdentifierLabel="Email"
      accountIdentifierPlaceholder="your-epic-pm-email@example.com"
      passwordLabel="Password"
      passwordPlaceholder="Enter your password"
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      initialAccountIdentifier={existingCredential?.accountIdentifier}
      additionalSchema={epicPmAdditionalSchema}
      initialValues={{ defaultProperty }}
      confirmationText={
        isEditMode
          ? 'Update your credentials for Epic PM'
          : 'Connect your credentials for Epic PM'
      }
    >
      {({ form, isSubmitting }) => (
        <FormField
          control={form.control}
          name="defaultProperty"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>Default property (optional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="Park Forest Apartments"
                  disabled={isSubmitting}
                  value={field.value as string}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              </FormControl>
              <FormDescription>
                The default property name to use for the integration
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </AccountCredentialForm>
  )
}
